import FlatCard from "components/UI-lib/FlatCard";
import { classNames, fallbackCopyTextToClipboard } from "components/utils";
import { UserContext } from "contexts/UserContext";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { Copy } from "lucide-react";
import {
  TrashIcon,
  PencilSquareIcon,
  LinkIcon as LinkIconSolid,
} from "@heroicons/react/24/solid";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/Graphs/Dropdown";

import LaunchableConfigurationView from "../Deploy/shared/LaunchableConfigurationView";
import { GPUInstanceType } from "types/InstanceTypes";
import UsernameDisplayField from "components/UsernameDisplayField";
import { Collapse, Tooltip } from "@mui/material";
import { OrgContext } from "contexts/OrgContext";
import { LaunchableFromList } from "server";
import { getLaunchableUrl, processLaunchableData } from "../utils";
import { DeleteLaunchableConfirmModal } from "components/Environment/modals/DeleteEnvironmentConfirmModal/DeleteLaunchableConfirmModal";
import agent from "server";
import { LaunchableAccessControl } from "../shared/LaunchableAccessControl";
import { Button } from "components/Graphs/Button";
import { toast } from "utils/toast";
import { useHistory } from "react-router-dom";
import { useShortFileName } from "../hooks/useShortFileName";

interface LaunchableMetaProps {
  id: string;
  launchable: LaunchableFromList;
  userName?: string;
  disable?: boolean;
  deleteLaunchable: (orgID: string, id: string) => Promise<any>;
  onPublishClick: (
    launchableId: string,
    launchableName: string,
    description: string,
    readmeLink: string,
    launchableData: LaunchableFromList,
  ) => void;
}

const LaunchableMeta: React.FC<LaunchableMetaProps> = (props) => {
  const userContext = useContext(UserContext);
  const orgContext = useContext(OrgContext);
  const history = useHistory();
  const [collapsible, setCollapsible] = useState(false);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const launchableDetails = processLaunchableData(props.launchable);
  const shortFileName = useShortFileName(launchableDetails?.file);

  const handleEditLaunchable = () => {
    history.push(`/launchables/${props.id}/edit`);
  };

  const handleCopyLink = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(getLaunchableUrl(props.launchable));
      } else {
        fallbackCopyTextToClipboard(getLaunchableUrl(props.launchable));
      }
      setCopiedLink(true);
    } catch (err) {
      console.error("Failed to copy link:", err);
      fallbackCopyTextToClipboard(getLaunchableUrl(props.launchable));
      setCopiedLink(true);
    }
  };

  const handleCopyId = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(props.id);
      } else {
        fallbackCopyTextToClipboard(props.id);
      }
      setCopiedId(true);
    } catch (err) {
      console.error("Failed to copy ID:", err);
      fallbackCopyTextToClipboard(props.id);
      setCopiedId(true);
    }
  };

  // Cleanup timers for copy feedback
  useEffect(() => {
    if (!copiedLink) return;
    const timer = setTimeout(() => setCopiedLink(false), 2000);
    return () => clearTimeout(timer);
  }, [copiedLink]);

  useEffect(() => {
    if (!copiedId) return;
    const timer = setTimeout(() => setCopiedId(false), 2000);
    return () => clearTimeout(timer);
  }, [copiedId]);

  const handleDelete = () => {
    setActionsMenuOpen(false);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    props.deleteLaunchable(orgContext.activeOrgId, props.id);
    toast.success("Launchable deleted");
  };

  const [instanceObject, setInstanceObject] = useState<GPUInstanceType | null>(
    null,
  );
  const [loadingInstance, setLoadingInstance] = useState(false);
  const [lifecycleScript, setLifecycleScript] = useState("");

  const getInstanceFromType = async (
    instanceType: string,
    cloudCredId: string,
  ): Promise<GPUInstanceType[] | null> => {
    const res = await agent.Instances.getInstanceByType(
      instanceType,
      cloudCredId,
    );
    if (res.success && res.data && res.data.length > 0) {
      return res.data;
    }
    return null;
  };

  const getInstanceObject = async (
    Instance: string | null,
    cloudCredId: string | null,
  ) => {
    const parsedInstance = parseInstance(Instance);
    if (!parsedInstance || !cloudCredId) {
      return null;
    }
    setLoadingInstance(true);
    const instanceTypes = await getInstanceFromType(
      parsedInstance.instanceType,
      cloudCredId,
    );
    setLoadingInstance(false);
    if (instanceTypes) {
      setInstanceObject(instanceTypes[0]);
    }
  };

  const parseInstance = (Instance: string | null) => {
    if (!Instance) {
      return null;
    }
    const [chipTypeOrInstanceType, instanceType] = Instance.split("@");
    if (instanceType) {
      return {
        chipType: chipTypeOrInstanceType,
        instanceType: instanceType,
      };
    } else {
      return {
        instanceType: chipTypeOrInstanceType,
      };
    }
  };

  const getLifeCycleScript = async (
    envId: string,
    lifecycleScriptId: string,
  ) => {
    const res = await agent.Workspaces.getLifeCycleScript(
      envId,
      lifecycleScriptId,
    );
    if (res.data) {
      setLifecycleScript(res.data?.attrs?.script || "");
    }
  };

  useEffect(() => {
    if (
      launchableDetails?.containerSelected?.vmBuild?.lifeCycleScriptAttr?.id
    ) {
      getLifeCycleScript(
        props.id || "",
        launchableDetails?.containerSelected?.vmBuild?.lifeCycleScriptAttr?.id,
      );
    }
  }, [launchableDetails]);

  useEffect(() => {
    getInstanceObject(
      launchableDetails?.instanceType || "",
      launchableDetails?.cloudCredId || "",
    );
  }, [launchableDetails?.instanceType, launchableDetails?.cloudCredId]);

  return (
    <>
      <DeleteLaunchableConfirmModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onDelete={confirmDelete}
        orgName={
          orgContext.orgs.find((o) => o.id === props.launchable.createdByOrgId)
            ?.name
        }
        launchable={props.launchable}
      />
      <FlatCard className="mb-2">
        {/* Marketplace Promotion Banner - Shows for eligible launchables */}

        <div className="flex flex-row justify-between gap-2 pb-2 lg:pb-0">
          <div className="leading-tight gap-1">
            <a
              href={
                props.disable ? undefined : getLaunchableUrl(props.launchable)
              }
              target="_blank"
              rel="noopener noreferrer"
              className="text-md font-semibold text-highlight hover:text-highlightDarker transition-colors group leading-tight"
              title="Click to view deploy page"
              onClick={(e) => {
                if (props.disable) {
                  e.preventDefault();
                }
              }}
            >
              <span>{launchableDetails?.name || "Unknown Name"}</span>{" "}
              <ArrowTopRightOnSquareIcon className="inline h-3.5 w-3.5 pb-[1px] text-secondary" />
            </a>{" "}
            <span className="text-xs text-secondary mt-1">by</span>{" "}
            <span className="text-xs text-secondary font-semibold mt-1">
              {props.userName ? (
                props.userName
              ) : (
                <UsernameDisplayField
                  className="text-xs text-secondary"
                  userId={launchableDetails?.userId || ""}
                />
              )}
            </span>
          </div>
          <div className="flex flex-row items-start">
            <div className="flex flex-row items-center gap-2">
              {/* Copy Link Button - Visible on md+ screens (icon only) */}
              <Tooltip
                title={copiedLink ? "Link copied!" : "Copy link"}
                placement="top"
              >
                <Button
                  variant="secondary"
                  onClick={handleCopyLink}
                  className="hidden md:flex h-8 w-8 p-0"
                  aria-label="Copy link"
                >
                  <LinkIconSolid
                    className={classNames(
                      "h-4 w-4",
                      copiedLink ? "text-highlight" : "",
                    )}
                  />
                </Button>
              </Tooltip>

              <LaunchableAccessControl
                initialAccessLevel={launchableDetails?.viewAccess || "public"}
                disabled={props.disable}
                launchableId={props.id}
                orgId={orgContext.activeOrgId}
                launchableName={launchableDetails?.name || "Launchable"}
                description={launchableDetails?.description || ""}
                readmeLink={launchableDetails?.file || ""}
                onPublishClick={() =>
                  props.onPublishClick(
                    props.id,
                    launchableDetails?.name || "Launchable",
                    launchableDetails?.description || "",
                    launchableDetails?.file || "",
                    props.launchable,
                  )
                }
              />

              {/* Edit Button - Visible on md+ screens */}
              {!props.launchable.rawURL && (
                <Button
                  variant="secondary"
                  onClick={handleEditLaunchable}
                  className="hidden md:flex h-8"
                  aria-label="Edit launchable"
                >
                  <span>Edit</span>
                </Button>
              )}

              {/* Actions Dropdown Menu */}
              <DropdownMenu
                open={actionsMenuOpen}
                onOpenChange={setActionsMenuOpen}
              >
                <DropdownMenuTrigger asChild>
                  <Button variant="noshow" aria-label="More actions">
                    <EllipsisVerticalIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end" side="bottom">
                  {/* Copy link - only show on small screens (hidden on md+) */}
                  <DropdownMenuItem
                    onClick={handleCopyLink}
                    className="md:hidden group"
                  >
                    <LinkIconSolid className="h-4 w-4 mr-2 text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white transition-colors" />
                    {copiedLink ? "Link copied!" : "Copy link"}
                  </DropdownMenuItem>

                  <DropdownMenuItem onClick={handleCopyId} className="group">
                    <Copy className="h-4 w-4 mr-2 text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white transition-colors" />
                    {copiedId ? "ID copied!" : "Copy ID"}
                  </DropdownMenuItem>

                  {/* Edit - only show on small screens (hidden on md+) */}
                  {!props.launchable.rawURL && (
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setActionsMenuOpen(false);
                        handleEditLaunchable();
                      }}
                      className="md:hidden group"
                    >
                      <PencilSquareIcon className="h-4 w-4 mr-2 text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white transition-colors" />
                      Edit
                    </DropdownMenuItem>
                  )}

                  <DropdownMenuSeparator />

                  <DropdownMenuItem
                    onClick={handleDelete}
                    className="text-rose-600 dark:text-rose-400 hover:bg-red-50 dark:hover:bg-red-900/20 group"
                  >
                    <TrashIcon className="h-4 w-4 mr-2 text-rose-600 dark:text-rose-400 group-hover:text-rose-700 dark:group-hover:text-rose-300 transition-colors" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Copy markdown badge */}
        <div className="flex flex-row items-end items-center text-sm justify-start mt-1">
          <div
            className="flex flex-row items-center cursor-pointer hover:text-highlight text-white transition-colors"
            onClick={() => {
              setCollapsible(!collapsible);
            }}
          >
            <span className="text-xs cursor-pointer flex flex-row border border-borderPrimary hover:border-highlight rounded-md px-2 py-1">
              {collapsible ? "Hide" : "Show"} Configuration
            </span>
          </div>
        </div>
        <Collapse
          in={collapsible}
          sx={{
            marginTop: "",
            maxHeight: "500px",
          }}
        >
          <hr className="w-[100%] my-3 dark:border dark:border-zinc-800" />
          <LaunchableConfigurationView
            intendedInstanceType={launchableDetails?.instanceType || ""}
            intendedDiskStorage={launchableDetails?.diskStorage || ""}
            containerSelected={launchableDetails?.containerSelected || null}
            cuda={launchableDetails?.cuda || ""}
            diskStorage={launchableDetails?.diskStorage || ""}
            file={launchableDetails?.file || ""}
            instanceTypeObject={instanceObject || null}
            ports={launchableDetails?.ports || null}
            firewallRules={launchableDetails?.firewallRules || null}
            python={launchableDetails?.python || ""}
            disabled={props.disable}
            lifecycleScript={lifecycleScript}
            loadingInstance={loadingInstance}
            shortFileName={shortFileName}
            forceJupyterInstall={
              !!launchableDetails?.containerSelected?.vmBuild
                ?.forceJupyterInstall ||
              !!launchableDetails?.containerSelected?.dockerCompose
                ?.jupyterInstall
            }
            hideEditCompute={true}
          />
        </Collapse>
      </FlatCard>
    </>
  );
};

export default LaunchableMeta;

interface CopyMarkdownButtonProps {
  launchableUrl: string;
}

const CopyMarkdownButton: React.FC<CopyMarkdownButtonProps> = ({
  launchableUrl,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const backgroundImageStyle = {
    backgroundImage: `url(https://brev-assets.s3.us-west-1.amazonaws.com/nv-lb-dark.svg)`,
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    width: "120px",
    height: "20px",
  };

  const getMarkDown = (url) => {
    return `[![ Click here to deploy.](https://brev-assets.s3.us-west-1.amazonaws.com/nv-lb-dark.svg)](${url})`;
  };

  const handleClick = useCallback(() => {
    fallbackCopyTextToClipboard(getMarkDown(launchableUrl));
    setIsCopied(true);
  }, [launchableUrl]);

  useEffect(() => {
    if (isCopied) {
      const timer = setTimeout(() => {
        setIsCopied(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isCopied]);

  return (
    <div className="flex flex-row items-center">
      <div
        className="flex flex-row items-center w-[150px] h-[20px] cursor-pointer"
        style={isHovered || isCopied ? {} : backgroundImageStyle}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {(isHovered || isCopied) && (
          <div
            className={`w-full h-full flex items-center justify-center text-xs border border-gray-500 dark:border-zinc-800 ${
              isCopied ? "text-highlight" : "text-gray-900 dark:text-slate-500"
            }`}
          >
            {isCopied ? "Copied Badge!" : "Copy markdown badge"}
          </div>
        )}
      </div>
    </div>
  );
};
